import { useForm } from '@formspree/react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useLocation, Link } from 'react-router-dom';
import SuccessPopup from '../components/SuccessPopup';
import { useTranslation } from 'react-i18next';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

// Type for values array
type ValueSection = {
  title: string;
  icon: string;
  points: string[];
};

export default function AboutContact() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [formState, handleSubmit] = useForm("mdkkvwgq");
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const { t, i18n } = useTranslation();

  const getLocalizedPath = (path: string) => {
    return i18n.language === 'en' ? path : `/${i18n.language}${path}`;
  };

  // Get values array with proper typing
  const values = t('aboutContact.about.values', { returnObjects: true }) as ValueSection[];

  useEffect(() => {
    if (location.hash === '#contact') {
      const element = document.getElementById('contact');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    if (formState.succeeded) {
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }
  }, [formState.succeeded]);

  return (
    <>
      <Helmet>
        <title>{t('aboutContact.meta.title')}</title>
        <meta 
          name="description" 
          content={t('aboutContact.meta.description')}
        />
      </Helmet>

      <main className="pt-24 bg-background">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <nav className="text-sm text-text/60">
            <Link to="/" className="hover:text-primary">{t('aboutContact.breadcrumb.home')}</Link>
            <span className="mx-2">/</span>
            <span className="text-text">{t('aboutContact.breadcrumb.about')}</span>
          </nav>
        </div>

        {/* About Section */}
        <section className="py-24">
          <motion.div 
            className="max-w-7xl mx-auto px-6 lg:px-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div 
              className="max-w-3xl mx-auto text-center mb-16"
              variants={itemVariants}
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6">
                {t('aboutContact.about.title')}
              </h1>
              <p className="text-xl text-text/80 leading-relaxed">
                {t('aboutContact.about.subtitle')}
              </p>
            </motion.div>

            {/* Who We Are Section */}
            <motion.div 
              className="max-w-3xl mx-auto mb-16 bg-white rounded-2xl p-8 shadow-lg"
              variants={itemVariants}
            >
              <h2 className="text-2xl font-bold text-primary mb-6 text-center">
                {t('aboutContact.about.mission.title')}
              </h2>
              <div className="space-y-4">
                <p className="text-text/80 leading-relaxed text-center mb-6">
                  {t('aboutContact.about.mission.description')}
                </p>
                <ul className="space-y-4">
                  {(t('aboutContact.about.mission.points', { returnObjects: true }) as string[]).map((point: string, index: number) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                      <span className="text-text/70 leading-relaxed">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Values Grid */}
            <motion.div 
              className="grid md:grid-cols-3 gap-8 mb-16"
              variants={itemVariants}
            >
              {values.map((item, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mb-6 mx-auto">
                    <span className="text-secondary text-xl font-semibold">
                      {item.icon}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-primary mb-4">
                    {item.title}
                  </h3>
                  <ul className="space-y-3">
                    {item.points.map((point, pointIndex) => (
                      <li 
                        key={pointIndex} 
                        className="flex items-start space-x-3"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-secondary mt-2 flex-shrink-0" />
                        <span className="text-text/70 leading-relaxed">
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-white scroll-mt-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Contact Form */}
              <motion.div 
                className="space-y-8"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.div variants={itemVariants}>
                  <h2 className="text-3xl font-bold text-primary mb-4">
                    {t('aboutContact.contact.title')}
                  </h2>
                  <p className="text-text/80">
                    {t('aboutContact.contact.subtitle')}
                  </p>
                </motion.div>

                <motion.form 
                  className="space-y-6"
                  onSubmit={handleSubmit}
                  variants={itemVariants}
                >
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-text/70 mb-2">
                        {t('aboutContact.contact.form.name')}
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="w-full px-4 py-3 rounded-lg bg-background border border-subtle 
                          focus:border-secondary focus:ring-1 focus:ring-secondary 
                          transition-colors duration-200"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text/70 mb-2">
                        {t('aboutContact.contact.form.email')}
                      </label>
                      <input
                        type="email"
                        name="email"
                        className="w-full px-4 py-3 rounded-lg bg-background border border-subtle 
                          focus:border-secondary focus:ring-1 focus:ring-secondary 
                          transition-colors duration-200"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text/70 mb-2">
                      {t('aboutContact.contact.form.subject')}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      className="w-full px-4 py-3 rounded-lg bg-background border border-subtle 
                        focus:border-secondary focus:ring-1 focus:ring-secondary 
                        transition-colors duration-200"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text/70 mb-2">
                      {t('aboutContact.contact.form.message')}
                    </label>
                    <textarea
                      rows={6}
                      name="message"
                      className="w-full px-4 py-3 rounded-lg bg-background border border-subtle 
                        focus:border-secondary focus:ring-1 focus:ring-secondary 
                        transition-colors duration-200 resize-none"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formState.submitting}
                    className="w-full px-8 py-4 bg-primary text-white rounded-lg 
                      hover:bg-primary-light transition-all duration-300 
                      shadow-lg hover:shadow-xl text-lg font-medium
                      transform hover:-translate-y-0.5
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formState.submitting ? t('aboutContact.contact.form.sending') : t('aboutContact.contact.form.submit')}
                  </button>
                </motion.form>
              </motion.div>

              {/* Contact Info */}
              <motion.div 
                className="lg:pl-16 space-y-8"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.div variants={itemVariants}>
                  <h3 className="text-xl font-semibold text-primary mb-4">
                    {t('aboutContact.contact.info.title')}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                        <EnvelopeIcon className="w-6 h-6 text-secondary" />
                      </div>
                      <div>
                        <p className="font-medium text-primary">{t('aboutContact.contact.info.email.label')}</p>
                        <p className="text-text/70">{t('aboutContact.contact.info.email.value')}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* FAQ Callout */}
                <motion.div 
                  className="bg-background rounded-2xl p-8"
                  variants={itemVariants}
                >
                  <h3 className="text-xl font-semibold text-primary mb-4">
                    Have Questions?
                  </h3>
                  <p className="text-text/70 mb-6">
                    Check our frequently asked questions for quick answers.
                  </p>
                  <Link
                    to={getLocalizedPath('/faq')}
                    className="text-secondary hover:text-secondary/80 font-medium inline-flex items-center"
                  >
                    Visit FAQ 
                    <span className="ml-2">→</span>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      
      <SuccessPopup
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Message Sent!"
        message="Thanks for reaching out. We'll get back to you within 24 hours."
      />
    </>
  );
} 